import React from 'react';
import { ShieldCheckIcon, AlertTriangleIcon, DownloadIcon, UsersIcon, CheckCircleIcon, ListChecksIcon, SparklesIcon, FileSearchIcon } from 'lucide-react';
import { ScreenProps } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
import { SecondaryButton } from '../ui/SecondaryButton';
export function Screen16_UpdatedPTP({
  data,
  onNext,
  onBack,
  onExport
}: ScreenProps) {
  return <div className="flex flex-col h-full" data-id="element-575">
      <div className="flex items-center gap-3 mb-4" data-id="element-576">
        <ShieldCheckIcon className="w-8 h-8 text-orange-500" data-id="element-577" />
        <h2 className="text-2xl font-bold text-white leading-tight" data-id="element-578">
          PTP Updated from Meeting
        </h2>
      </div>

      <div className="inline-flex items-center gap-2 bg-orange-500/20 text-orange-400 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-6 self-start" data-id="element-579">
        <SparklesIcon className="w-4 h-4" data-id="element-580" />
        Updated by AI Review
      </div>

      <div className="flex-1 overflow-y-auto pb-6 -mx-4 px-4 space-y-6" data-id="element-581">
        {/* Header Info */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 space-y-3" data-id="element-582">
          <div className="grid grid-cols-2 gap-4" data-id="element-583">
            <div data-id="element-584">
              <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-585">
                Project
              </p>
              <p className="text-white font-medium text-sm" data-id="element-586">{data.project}</p>
            </div>
            <div data-id="element-587">
              <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-588">
                Location
              </p>
              <p className="text-white font-medium text-sm" data-id="element-589">{data.location}</p>
            </div>
          </div>
          <div className="pt-3 border-t border-slate-800" data-id="element-590">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-591">
              Task
            </p>
            <p className="text-orange-400 font-bold text-sm" data-id="element-592">
              Steel Beam Installation Using Mobile Crane
            </p>
          </div>
        </div>

        {/* Step-Based Hazard Analysis */}
        <section data-id="element-593">
          <div className="flex items-center gap-2 mb-3" data-id="element-594">
            <ListChecksIcon className="w-5 h-5 text-slate-400" data-id="element-595" />
            <h3 className="text-sm font-bold text-white uppercase tracking-wider" data-id="element-596">
              Job Steps — Hazards & Controls
            </h3>
          </div>
          <div className="space-y-3" data-id="element-597">
            {data.steps.map((step, i) => <div key={i} className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden" data-id="element-598">
                <div className="px-4 py-3 border-b border-slate-800 flex gap-3" data-id="element-599">
                  <span className="text-orange-500 font-bold text-sm flex-shrink-0" data-id="element-600">
                    {i + 1}.
                  </span>
                  <span className="text-white font-medium text-sm" data-id="element-601">
                    {step.description}
                  </span>
                </div>
                <div className="px-4 py-3 space-y-3" data-id="element-602">
                  {step.hazards.map((h, j) => <div key={j} className="space-y-2" data-id="element-603">
                      <div className="flex items-start gap-2" data-id="element-604">
                        <AlertTriangleIcon className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${h.isNew ? 'text-orange-400' : 'text-red-400'}`} data-id="element-605" />
                        <div className="flex-1 flex items-center gap-2 flex-wrap" data-id="element-606">
                          <span className={`text-xs ${h.isNew ? 'text-orange-300 font-medium' : 'text-red-200'}`} data-id="element-607">
                            {h.hazard}
                          </span>
                          {h.riskLevel && <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${h.riskLevel === 'HIGH' ? 'bg-red-500/20 text-red-400' : h.riskLevel === 'MEDIUM' ? 'bg-amber-500/20 text-amber-400' : 'bg-green-500/20 text-green-400'}`} data-id="element-608">
                              {h.riskLevel}
                            </span>}
                          {h.isNew && <span className="text-[9px] uppercase bg-orange-500/20 text-orange-400 px-1.5 py-0.5 rounded font-bold" data-id="element-609">
                              New
                            </span>}
                        </div>
                      </div>
                      <div className="ml-5 space-y-1" data-id="element-610">
                        {h.controls.map((c, k) => <div key={k} className="flex items-start gap-2 text-xs" data-id="element-611">
                            <CheckCircleIcon className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" data-id="element-612" />
                            <span className="text-green-100" data-id="element-613">{c}</span>
                          </div>)}
                      </div>
                    </div>)}
                </div>
              </div>)}
          </div>
        </section>

        {/* Crew */}
        <section data-id="element-614">
          <div className="flex items-center gap-2 mb-3" data-id="element-615">
            <UsersIcon className="w-5 h-5 text-slate-400" data-id="element-616" />
            <h3 className="text-sm font-bold text-white uppercase tracking-wider" data-id="element-617">
              Crew
            </h3>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-618">
            {data.crewRoles.map((role, i) => <div key={i} className="bg-slate-800 border border-slate-700 text-slate-300 px-3 py-1.5 rounded-md text-sm font-medium" data-id="element-619">
                {role.role}
              </div>)}
          </div>
        </section>

        {/* PPE */}
        <section data-id="element-620">
          <div className="flex items-center gap-2 mb-3" data-id="element-621">
            <ShieldCheckIcon className="w-5 h-5 text-green-400" data-id="element-622" />
            <h3 className="text-sm font-bold text-white uppercase tracking-wider" data-id="element-623">
              Required PPE
            </h3>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-624">
            {data.ppeControls.map((ppe, i) => <div key={i} className="bg-green-950/20 border border-green-900/30 text-green-100 px-3 py-1.5 rounded-md text-sm font-medium" data-id="element-625">
                {ppe}
              </div>)}
          </div>
        </section>

        {/* Equipment */}
        <section data-id="element-626">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-3" data-id="element-627">
            Equipment
          </h3>
          <div className="flex flex-wrap gap-2" data-id="element-628">
            {data.equipment.map((item, i) => <div key={i} className="bg-slate-800 border border-slate-700 text-slate-300 px-3 py-1.5 rounded-md text-sm font-medium" data-id="element-629">
                {item}
              </div>)}
          </div>
        </section>

        {/* Safety Checks */}
        <section data-id="element-630">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-3" data-id="element-631">
            Pre-Work Safety Checks
          </h3>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-2" data-id="element-632">
            {data.safetyChecks.map((check, i) => <div key={i} className="flex items-center gap-2 text-slate-300 text-sm" data-id="element-633">
                <CheckCircleIcon className="w-4 h-4 text-green-500" data-id="element-634" />
                {check}
              </div>)}
          </div>
        </section>

        {/* AI Meeting Notes */}
        <section data-id="element-635">
          <div className="flex items-center gap-2 mb-3" data-id="element-636">
            <SparklesIcon className="w-5 h-5 text-orange-500" data-id="element-637" />
            <h3 className="text-sm font-bold text-white uppercase tracking-wider" data-id="element-638">
              AI Meeting Notes
            </h3>
          </div>
          <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 space-y-2" data-id="element-639">
            <p className="text-sm text-orange-200" data-id="element-640">
              <span className="font-bold text-white" data-id="element-641">Meeting Score:</span>{' '}
              {data.meetingScore || 72} / 100
            </p>
            <p className="text-sm text-orange-200" data-id="element-642">
              <span className="font-bold text-white" data-id="element-643">Main Gap:</span> crew
              participation and missing review of wind and emergency response
            </p>
          </div>
        </section>
      </div>

      <div className="pt-4 space-y-3 bg-slate-950" data-id="element-644">
        <PrimaryButton onClick={onNext} data-id="element-645">Continue to Quiz</PrimaryButton>
        <div className="flex gap-2" data-id="element-646">
          <SecondaryButton onClick={() => onExport?.()} icon={<DownloadIcon className="w-5 h-5" data-id="element-648" />} className="flex-1" data-id="element-647">
            Export PTP
          </SecondaryButton>
          <SecondaryButton onClick={onBack} icon={<FileSearchIcon className="w-5 h-5" data-id="element-650" />} className="flex-1" data-id="element-649">
            Meeting Review
          </SecondaryButton>
        </div>
      </div>
    </div>;
}